import React, { useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import { Field, Input, Select, FormGrid, FormFooter } from '../../../ui/Field/Field';
import useDraft from '../../../ui/Field/useDraft';
import { custodyService } from '../../../services/custodyService';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';

const CATEGORY_OPTIONS = [
    { value: 'Fuel', label: 'Fuel' },
    { value: 'Spare Parts', label: 'Spare Parts' },
    { value: 'Fees', label: 'Fees (Government/Fine)' },
    { value: 'Driver', label: 'Driver / Labor' },
    { value: 'Office', label: 'Office Supplies' },
    { value: 'Other', label: 'Other' },
];

const PAYMENT_OPTIONS = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'transfer', label: 'Bank Transfer' },
];

const ExpenseForm = ({ custodyId, initialData, onSuccess, onCancel }) => {
    // A new expense is kept as a draft in this browser until saved; editing never drafts.
    const [formData, setFormData, draft] = useDraft(
        `draft:new-expense:${custodyId}`,
        initialData || {
            expense_date: new Date().toISOString().split('T')[0],
            category: 'Fuel',
            description: '',
            amount: '',
            invoice_number: '',
            payment_method: 'cash',
            attachment_url: '' // Placeholder for now
        },
        { enabled: !initialData }
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        // Implement file upload logic here (e.g., to Firebase Storage)
        // For MVP, just simulate success or store file name
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, attachment_url: file.name })); // Mock
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.amount || !formData.expense_date || !formData.category) {
            setError('Please fill in required fields.');
            return;
        }

        setLoading(true);

        // 1. Check Invoice Uniqueness
        const isDuplicate = await custodyService.checkInvoiceNumberExists(formData.invoice_number, initialData?.id);
        if (isDuplicate) {
            setError(`Invoice number "${formData.invoice_number}" already exists. Please use a unique invoice number.`);
            setLoading(false);
            return;
        }

        // Default Description to Category if empty
        const finalData = {
            ...formData,
            description: formData.description.trim() === '' ? formData.category : formData.description
        };

        let res;
        if (initialData) {
            res = await custodyService.updateExpense(custodyId, initialData.id, finalData, initialData.amount);
        } else {
            res = await custodyService.addExpense(custodyId, finalData);
        }
        setLoading(false);

        if (res.success) {
            if (!initialData) draft.clear();
            onSuccess();
        } else {
            // the store's refusals say why: someone changed the expense since it was opened / the invoice number is taken
            const code = res.error?.code;
            setError((code === 'record-changed' || code === 'invoice-exists') && res.error.message ? res.error.message : 'Failed to save expense.');
        }
    };

    // Date / amount / category are checked by handleSubmit (same message as before), so they
    // carry aria-required instead of the native attribute; Invoice No. keeps native `required`.
    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">{error}</div>}

            <FormGrid cols={2}>
                <Field label="Date" required>
                    <Input
                        type="date"
                        name="expense_date"
                        value={formData.expense_date}
                        onChange={handleChange}
                        required={false}
                        aria-required="true"
                    />
                </Field>
                <Field label="Amount" required>
                    <Input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        placeholder="0.00"
                        suffix="AED"
                        required={false}
                        aria-required="true"
                    />
                </Field>
            </FormGrid>

            <FormGrid cols={2}>
                <Field label="Category" required>
                    <Select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        options={CATEGORY_OPTIONS}
                        required={false}
                        aria-required="true"
                    />
                </Field>
                <Field label="Payment Method">
                    <Select
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleChange}
                        options={PAYMENT_OPTIONS}
                    />
                </Field>
            </FormGrid>

            <Field label="Description" help="Left empty, the category is used.">
                <Input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description..."
                />
            </Field>

            <Field label="Invoice No." required>
                <Input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleChange}
                    placeholder="e.g. INV-12345"
                    required
                />
            </Field>

            {/* Only the chosen file's NAME is stored with the expense; the file is not uploaded. */}
            <div className="flex flex-col gap-1.5">
                <span id="expense-attachment-label" className="text-sm font-medium text-gray-700">Attachment file name (invoice/receipt)</span>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept="image/*,application/pdf"
                        tabIndex={-1}
                        aria-hidden="true"
                    />
                    <Button
                        type="button"
                        size="sm"
                        leadingIcon={<Icon glyph={ICONS.attach} size="sm" />}
                        onClick={() => fileInputRef.current?.click()}
                        aria-describedby="expense-attachment-label expense-attachment-help"
                    >
                        {formData.attachment_url ? 'Choose another file' : 'Attach file name'}
                    </Button>
                    {formData.attachment_url ? (
                        <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                            <Icon glyph={ICONS.document} size="sm" /> {formData.attachment_url}
                        </span>
                    ) : (
                        <span className="text-sm text-gray-500">No file chosen</span>
                    )}
                </div>
                <span id="expense-attachment-help" className="text-xs text-gray-600">
                    Only the file name is saved with the expense. The file itself is not uploaded.
                </span>
            </div>

            <FormFooter sticky={false} status={initialData ? null : draft.status}>
                <Button type="button" onClick={onCancel} disabled={loading}>Cancel</Button>
                <Button type="submit" variant="primary" isLoading={loading}>Save Expense</Button>
            </FormFooter>
        </form>
    );
};

export default ExpenseForm;
